import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, LogOut, ShieldCheck, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/marketing/Logo";
import PartnerStats from "@/components/dashboard/PartnerStats";
import PromoStudio from "@/components/dashboard/PromoStudio";
import AdTrackerCard from "@/components/dashboard/AdTrackerCard";
import ReferralAdminPanel from "@/components/dashboard/ReferralAdminPanel";
import UserTradesAdminPanel from "@/components/dashboard/UserTradesAdminPanel";
import SignupFunnelCard from "@/components/dashboard/SignupFunnelCard";
import AdminAnalyticsPanel from "@/components/dashboard/AdminAnalyticsPanel";
import ContestAdminCard from "@/components/dashboard/ContestAdminCard";
import BotControlsAdminCard from "@/components/dashboard/BotControlsAdminCard";
import AdminInsights from "@/components/dashboard/AdminInsights";
import WinCardStudio from "@/components/dashboard/WinCardStudio";
import {
  adminLogin,
  adminMe,
  clearAdminToken,
  getAdminToken,
} from "@/lib/adminAuth";

const PartnerEarnings = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAdminToken()) {
        if (!cancelled) setChecking(false);
        return;
      }
      const me = await adminMe();
      if (cancelled) return;
      setAdmin(me);
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError("");
    try {
      const data = await adminLogin(email.trim(), password);
      setAdmin({ email: data.email, role: "admin" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Invalid credentials. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    setAdmin(null);
    setEmail("");
    setPassword("");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-vts-bg flex items-center justify-center">
        <div className="text-xs text-vts-silver/60 font-mono uppercase tracking-wider">
          Verifying session…
        </div>
      </div>
    );
  }

  if (!admin) {
    return (
      <div
        data-testid="partner-earnings-locked"
        className="min-h-screen bg-vts-bg flex items-center justify-center p-6"
      >
        <form
          onSubmit={handleLogin}
          className="vts-card p-8 max-w-md w-full"
          data-testid="admin-login-form"
        >
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-vts-blue" />
            <h1 className="font-heading font-bold text-xl text-white tracking-tight">
              Admin Sign-in
            </h1>
          </div>
          <p className="text-sm text-vts-silver/70 text-center mb-6 font-body">
            Private dashboard — sign in with your owner credentials to view
            real-time markup commission and affiliate earnings.
          </p>

          <Label className="text-xs uppercase tracking-wider text-vts-silver/60 mb-2 block">
            Email
          </Label>
          <div className="relative mb-4">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-vts-silver/40" />
            <Input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@vts.local"
              autoFocus
              autoComplete="username"
              className="bg-vts-surface border-vts-line text-white font-mono pl-9"
            />
          </div>

          <Label className="text-xs uppercase tracking-wider text-vts-silver/60 mb-2 block">
            Password
          </Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-vts-silver/40" />
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              className="bg-vts-surface border-vts-line text-white font-mono pl-9"
            />
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="text-xs text-vts-loss mt-3 font-mono"
            >
              {error}
            </div>
          )}

          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={submitting || !email || !password}
            className="w-full mt-5 bg-vts-blue hover:bg-vts-blueHover text-white font-semibold disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-[10px] text-vts-silver/40 font-mono text-center mt-4 flex items-center justify-center gap-1.5">
            <Lock className="h-3 w-3" /> 12h secure session · JWT
          </div>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="w-full mt-3 text-xs text-vts-silver/60 hover:text-white py-2 flex items-center justify-center gap-1.5"
          >
            <ArrowLeft className="h-3 w-3" /> Back to site
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="partner-earnings-page" className="min-h-screen bg-vts-bg">
      <div className="border-b border-vts-line bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-vts-blue/15 text-vts-blue border border-vts-blue/30 font-mono">
              Admin · {admin.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="text-xs text-vts-silver/70 hover:text-white px-3 py-2"
            >
              ← Public site
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="text-xs text-vts-loss/80 hover:text-vts-loss px-3 py-2 flex items-center gap-1.5"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-3xl text-white tracking-tight">
            Partner Earnings
          </h1>
          <p className="text-sm text-vts-silver/70 mt-1 font-body">
            Real-time markup commission + affiliate revenue tracking
          </p>
        </div>
        <PartnerStats onUnauthorized={handleLogout} />
        {/* 📊 AI Coach conversations + captured emails + email funnel */}
        <div className="mt-6">
          <h2 className="font-heading font-bold text-lg text-white tracking-tight mb-3">User Insights — AI Coach chats &amp; emails</h2>
          <AdminInsights />
        </div>
        <div className="mt-6">
          <WinCardStudio />
        </div>
        <div className="mt-6">
          <PromoStudio />
        </div>
        <AdTrackerCard />
        <AdminAnalyticsPanel onUnauthorized={handleLogout} />
        <ContestAdminCard onUnauthorized={handleLogout} />
        <BotControlsAdminCard onUnauthorized={handleLogout} />
        <SignupFunnelCard onUnauthorized={handleLogout} />
        <UserTradesAdminPanel onUnauthorized={handleLogout} />
        <ReferralAdminPanel onUnauthorized={handleLogout} />
      </div>
    </div>
  );
};

export default PartnerEarnings;
